import { useState } from 'react';
import { Alert, Card, Col, Descriptions, Empty, Flex, Image, Input, Row, Segmented, Skeleton, Tag, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useApi } from '../../app/api-context';
import type { AppSearchItem, AppSearchResult } from '../../lib/search-types';
import { CalibrationNotice, PhysicalImage } from '../../components/PhysicalSize';

const RESOLUTION_LABEL: Record<string, string> = {
  manual_override: '수동 보정',
  translation_cache: '번역 캐시',
  translation_api: '번역 API',
  alias: '내장 별칭',
  passthrough: '원문 그대로',
};

type Scope = 'all' | 'isori' | 'external';
type View = 'card' | '3' | '5';

function ResultCard({ item }: { item: AppSearchItem }) {
  const isori = item.origin === 'isori';
  return (
    <Card size="small" styles={{ body: { padding: 8 } }} cover={<Image src={item.previewUrl} preview={{ src: item.largeImageUrl }} height={140} style={{ objectFit: 'contain', background: '#fafafa' }} alt={item.displayName} />}>
      <Flex justify="space-between" align="center" gap={4}>
        <Typography.Text strong ellipsis>{item.rank}. {item.displayName}</Typography.Text>
        <Tag color={isori ? 'blue' : 'default'} style={{ marginInlineEnd: 0 }}>{item.sourceLabel}</Tag>
      </Flex>
      {item.doc && (
        <div style={{ marginTop: 6 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }} copyable={{ text: item.doc.docId }}>{item.doc.docId}</Typography.Text>
          <Flex wrap gap={4} style={{ marginTop: 4 }}>
            {item.doc.tags.map((t) => (
              <Tag key={t} color={item.matchedTags.includes(t) ? 'gold' : undefined} style={{ marginInlineEnd: 0 }}>{t}</Tag>
            ))}
          </Flex>
          {item.doc.problems.length > 0 && (
            <Tooltip title={item.doc.problems.join(' / ')}>
              <Tag color="orange" icon={<WarningOutlined />} style={{ marginTop: 4 }}>점검 {item.doc.problems.length}</Tag>
            </Tooltip>
          )}
        </div>
      )}
      {isori && !item.doc && <Tag color="red" style={{ marginTop: 6 }}>Firestore 문서 없음</Tag>}
    </Card>
  );
}

export function AppSearchPage() {
  const api = useApi();
  // 검수함에서 게시 후 '앱 검색 확인'으로 들어오면 검색어를 미리 채운다
  const [params] = useSearchParams();
  const [q, setQ] = useState(() => params.get('q')?.trim() ?? '');
  const [scope, setScope] = useState<Scope>('all');
  const [view, setView] = useState<View>('card');
  const search = useQuery({
    queryKey: ['app-search', q],
    queryFn: () => api.get<AppSearchResult>(`/search?${new URLSearchParams({ q })}`),
    enabled: q.trim().length > 0,
    staleTime: 60_000,
    retry: false,
  });
  const data = search.data;
  const items = (data?.items ?? []).filter((i) => scope === 'all' || i.origin === scope);

  return (
    <>
      <Typography.Title level={3}>검색 결과 확인</Typography.Title>
      <Typography.Paragraph type="secondary">
        아이소통카드 &lsquo;이미지 추가 → 웹에서 검색&rsquo;과 같은 검색 함수를 호출해 실제 앱 결과를 같은 순서로 보여줍니다. 카드명이 아니라 태그로 연결된 이미지도 함께 나옵니다.
      </Typography.Paragraph>
      <Input.Search
        size="large"
        allowClear
        enterButton="검색"
        placeholder="예: 간식"
        style={{ maxWidth: 480 }}
        defaultValue={q}
        onSearch={(v) => setQ(v.trim())}
        loading={search.isFetching}
      />
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
        앱에서 검색할 때와 똑같이 운영 검색 캐시가 기록되고 외부 이미지 검색(Pixabay)이 호출됩니다.
      </Typography.Paragraph>

      {!q && <Empty description="검색어를 입력하세요" style={{ marginTop: 48 }} />}
      {q && search.isPending && <Skeleton active style={{ marginTop: 24 }} />}
      {q && search.isError && <Alert type="error" showIcon style={{ marginTop: 24 }} message="검색에 실패했습니다" description={(search.error as Error).message} />}
      {data && (
        <>
          <Descriptions size="small" column={{ xs: 1, md: 3 }} style={{ marginTop: 24 }} bordered>
            <Descriptions.Item label="검색어">{data.query}</Descriptions.Item>
            <Descriptions.Item label="정규화·번역">{data.normalizedQuery ?? '-'}{data.queryResolutionSource ? ` (${RESOLUTION_LABEL[data.queryResolutionSource] ?? data.queryResolutionSource})` : ''}</Descriptions.Item>
            <Descriptions.Item label="결과">{data.count}건 (아이소리 {data.isoriCount} · 외부 {data.count - data.isoriCount}){data.source ? ` · ${data.source === 'cache' ? '캐시' : data.source}` : ''}</Descriptions.Item>
          </Descriptions>
          <Flex justify="space-between" align="center" style={{ margin: '16px 0' }}>
            <Segmented<Scope>
              value={scope}
              onChange={setScope}
              options={[
                { value: 'all', label: `전체 ${data.count}` },
                { value: 'isori', label: `아이소리 ${data.isoriCount}` },
                { value: 'external', label: `외부 ${data.count - data.isoriCount}` },
              ]}
            />
            <Flex align="center" gap={12} wrap>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>노란 태그: 검색어와 정확히 일치한 태그</Typography.Text>
              <Segmented<View>
                value={view}
                onChange={setView}
                options={[{ value: 'card', label: '목록' }, { value: '3', label: '3cm' }, { value: '5', label: '5cm' }]}
              />
            </Flex>
          </Flex>
          {view !== 'card' && <div style={{ marginBottom: 12 }}><CalibrationNotice /></div>}
          {items.length === 0 ? (
            <Empty description="결과가 없습니다" />
          ) : view !== 'card' ? (
            // 같은 물리 크기로 나란히 놓고 식별성을 비교한다(R1 §11.4)
            <Flex wrap gap={16}>
              {items.map((item) => (
                <PhysicalImage
                  key={`${item.rank}-${item.previewUrl}`}
                  src={item.largeImageUrl || item.previewUrl}
                  alt={item.displayName}
                  size={view === '3' ? 3 : 5}
                  caption={`${item.rank}. ${item.displayName}`}
                />
              ))}
            </Flex>
          ) : (
            <Row gutter={[12, 12]}>
              {items.map((item) => (
                <Col key={`${item.rank}-${item.previewUrl}`} xs={12} sm={8} md={6} lg={4}>
                  <ResultCard item={item} />
                </Col>
              ))}
            </Row>
          )}
        </>
      )}
    </>
  );
}
